import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

/**
 * Admin Reset Password Page - mirrors Login.jsx styling.
 */
export default function ResetPassword() {
    const { resetPassword } = useAuth()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')

    const [formData, setFormData] = useState({ password: '', confirmPassword: '' })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        setError('')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters')
            return
        }
        if (!/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
            setError('Password must contain an uppercase letter and a number')
            return
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match')
            return
        }

        setLoading(true)
        try {
            await resetPassword(token, formData.password)
            setSuccess(true)
            setTimeout(() => navigate('/login'), 3000)
        } catch (err) {
            setError(err.message || 'Failed to reset password. The link may have expired.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="p-8 text-center bg-brand-navy border-b border-brand-navy-dark">
                    <img src="/logo.png" alt="TenderCentriX" className="h-10 mx-auto mb-4" />
                    <h1 className="text-lg font-bold text-white">Reset password</h1>
                    {!token ? (
                        <p className="text-sm text-slate-300 mt-1">This password reset link is invalid or has expired.</p>
                    ) : success ? (
                        <p className="text-sm text-slate-300 mt-1">Password updated. Redirecting to login...</p>
                    ) : (
                        <p className="text-sm text-slate-300 mt-1">Enter your new password below.</p>
                    )}
                </div>

                {!token ? (
                    <div className="p-8 text-center">
                        <Link
                            to="/forgot-password"
                            className="inline-flex justify-center w-full py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-[#a91e2d] to-[#d43f52] hover:bg-gradient-to-l transition-all"
                        >
                            Request new reset link
                        </Link>
                    </div>
                ) : success ? (
                    <div className="p-8 text-center">
                        <Link
                            to="/login"
                            className="inline-flex justify-center w-full py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-[#a91e2d] to-[#d43f52] hover:bg-gradient-to-l transition-all"
                        >
                            Sign in now
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-8 space-y-5">
                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 text-sm rounded border border-red-100">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                            <input
                                type="password"
                                name="password"
                                required
                                autoComplete="new-password"
                                className="w-full px-4 py-3 text-sm font-medium border text-slate-900 truncate rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none placeholder:text-slate-400 shadow-sm"
                                placeholder="Minimum 8 characters"
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                required
                                autoComplete="new-password"
                                className="w-full px-4 py-3 text-sm font-medium border text-slate-900 truncate rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none placeholder:text-slate-400 shadow-sm"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-[#a91e2d] to-[#d43f52] hover:bg-gradient-to-l focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#a91e2d] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {loading ? 'Updating...' : 'Update password'}
                        </button>
                    </form>
                )}

                <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
                    <p className="text-xs text-slate-400">© 2026 TenderCentriX. Secure Logged Access.</p>
                </div>
            </div>
        </div>
    )
}
